
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    
import React, { useEffect } from 'react'
import Image from 'next/image'
import { useRouter } from 'next/router'
import Link from 'next/link'

import { Breadcrumb, Card } from 'antd'
import { Typography } from 'antd'
import { RichText } from 'prismic-reactjs'
const { Title } = Typography
import { DiscussionEmbed } from 'disqus-react'
import Head from 'next/head'
import { tokenize, tfidf, wordCounts } from '../../helpers/bow'

import Wrapper, { BreadcrumbWrapper, Content } from './blog.style'

import { getBlogs, getBlogDetailWithSlug } from '../../services/cms'

import { langConverterReversed } from '../../helpers/urlhelpers'
import { ArrowRight2, Home } from 'iconsax-react'

export default function BlogDetail({ post, slug }) {
  const router = useRouter()

  const { isFallback } = router

  if (isFallback) {
    return <p>Loading</p>
  }

  const getAllContent = (post) => {
    let justContent = ''
    post.content.map((item) => {
      if (item.type === 'paragraph' || item.type === 'heading2') {
        justContent += item.text
      }
    })
    return justContent
  }

  const schemaData = [
    {
      '@context': 'http://schema.org',
      '@type': 'Article',
      name: post.title[0].text,
      headline: post.title[0].text,
      author: {
        '@type': 'Organization',
        name: 'invidza',
      },
      datePublished: post.date,
      image: post.image.url,
      articleSection: post.short_description,
      articleBody: getAllContent(post),
      url: 'https://invidza.com/blog/' + slug,
      publisher: {
        '@type': 'Organization',
        name: 'invidza',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Anasayfa',
          item: 'https://invidza.com',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Blog',
          item: 'https://invidza.com/blog',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: post.type,
          item: 'https://invidza.com/blog/?category=' + post.type,
        },
        {
          '@type': 'ListItem',
          position: 4,
          name: post.title[0].text,
        },
      ],
    },
  ]

  return (
    <Wrapper>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Head>
        <title>{post.title[0].text} | invidza</title>
        <meta name="description" content={post.short_description} />
        <link rel="canonical" href={'https://invidza.com' + router.asPath} />
        <meta property="og:title" content={post.title[0].text + ' | invidza'} key="title" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@publisher_handle" />
        <meta name="twitter:title" content={post.title[0].text + ' | invidza'} />
        <meta name="twitter:description" content={post.shortDescription} />
        <meta name="twitter:creator" content="@author_handle" />
        <meta name="twitter:image:src" content={post.og_image.url} />

        <meta property="og:type" content="article" />
        <meta property="og:url" content={'https://invidza.com' + router.asPath} />
        <meta property="og:image" content={post.og_image.url} />
        <meta property="og:description" content={post.shortDescription} />
        <meta property="og:site_name" content="invidza" />
        <meta property="article:published_time" content={post.date} />
        <meta
          property="article:modified_time"
          content={new Date(new Date().getTime() - 24 * 60 * 60 * 1000).toISOString()}
        />
        <meta property="article:section" content={post.type} />
        <meta property="article:tag" content={post.tags} />
      </Head>

      <Card style={{ width: '100%', maxWidth: '876px' }}>
        <BreadcrumbWrapper>
          <Breadcrumb separator={<ArrowRight2 color="#999999" size={14} />}>
            <Breadcrumb.Item>
              <Link href={'/'}>
                <a>
                  <Home color="#999999" size={14} /> Anasayfa
                </a>
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link href={'/blog'}>Blog</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link href={`/blog/?category=${post.type}`}>{post.type}</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{post.title[0].text}</Breadcrumb.Item>
          </Breadcrumb>
        </BreadcrumbWrapper>
        <Image
          src={post.image.url}
          width={post.image.dimensions.width}
          height={post.image.dimensions.height}
          alt={post.image.alt}
        />
        <div style={{ marginBottom: '30px' }}></div>
        <Title>{post.title[0].text}</Title>
        <div style={{ marginBottom: '1em' }}></div>

        <Content>
          <RichText render={post.content} />
        </Content>
      </Card>

      <DiscussionEmbed
        style={{ width: '100%', maxWidth: '876px', marginTop: 10 }}
        shortname="invidzadisquscom"
        config={{
          url: `https://invidza.com/${router.asPath}`,
          identifier: post.id,
          title: post.title[0].text,
          language: router.locale,
        }}
      />
    </Wrapper>
  )
}

 async function _getStaticProps(context) {
  const post = await getBlogDetailWithSlug(context.params.slug, context.locale)

  const text = post.content.reduce((accumulator, line) => `${accumulator} ${line.text}`)

  post.tags = wordCounts(text)

  const slug = context.params.slug

  return {
    props: {
      post,
      slug,
    },
    revalidate: 600, // refresh every 10 min
  }
}

export async function getStaticPaths() {
  // const blogs = await getBlogs();

  // const paths = blogs.results.map((blog) => ({
  //   params: { slug: blog.uid },
  //   locale: langConverterReversed(blog.lang),
  // }));

  /**
   * Pathleri bos verirsek ISR olarak rende olmus olacak
   *
   * https://static-tweet.vercel.app/
   * */

  return {
    paths: [],
    fallback: true,
  }
}


    export async function getStaticProps(ctx) {
        let res = _getStaticProps(ctx)
        if(typeof res.then === 'function') res = await res
        return {
          ...res,
          props: {
            ...(res.props || {}),
            ...(await __loadNamespaces({
              ...ctx,
              pathname: '/blog/[slug]',
              loaderName: 'getStaticProps',
              ...__i18nConfig,
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
            }))
          }
        }
    }
  